import {
  addWeeks,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { AvailableShift } from 'components/FinancialCheckup/FinancialDashboard/AvailableShiftsSection/useAvailableShifts';
import { startOfDay } from 'utils/dateUtils';
import { doesShiftDateKeyMatch, parseShiftDateKey, toDateKeyFromDate } from 'utils/shiftDateUtils';

export const LOW_DEMAND_SHIFT_THRESHOLD = 2;
export const WEEK_STARTS_ON = 1 as const;

const getMonthKey = (date: Date): string => format(date, 'yyyy-MM');

export const isViewingCurrentMonth = (selectedMonth: Date, today: Date): boolean =>
  getMonthKey(selectedMonth) === getMonthKey(today);

export const getShiftDate = (shift: AvailableShift): Date | null => parseShiftDateKey(shift.date);

export const isShiftInSelectedMonth = (shiftDateStr: string, selectedMonth: Date): boolean => {
  const shiftDate = parseShiftDateKey(shiftDateStr);
  if (!shiftDate) return false;

  return getMonthKey(shiftDate) === getMonthKey(selectedMonth);
};

export const isShiftInDateRange = (shiftDateStr: string, rangeStart: Date, rangeEnd: Date): boolean => {
  const rangeStartKey = toDateKeyFromDate(startOfDay(rangeStart));
  const rangeEndKey = toDateKeyFromDate(endOfDay(rangeEnd));

  return shiftDateStr >= rangeStartKey && shiftDateStr <= rangeEndKey;
};

export const getCurrentWeekEnd = (referenceDate: Date): Date =>
  endOfWeek(referenceDate, { weekStartsOn: WEEK_STARTS_ON });

export const getViewStartDate = (selectedMonth: Date, today: Date): Date => {
  if (isViewingCurrentMonth(selectedMonth, today)) {
    return startOfDay(today);
  }

  return startOfWeek(startOfMonth(selectedMonth), { weekStartsOn: WEEK_STARTS_ON });
};

export const countShiftsInRange = (shifts: AvailableShift[], rangeStart: Date, rangeEnd: Date): number =>
  shifts.filter((shift) => isShiftInDateRange(shift.date, rangeStart, rangeEnd)).length;

export const capToMonthEnd = (date: Date, selectedMonth: Date): Date => {
  const monthEnd = endOfDay(endOfMonth(selectedMonth));
  return isAfter(date, monthEnd) ? monthEnd : date;
};

const ensureVisibleEndIncludesToday = (visibleEnd: Date, today: Date): Date => {
  const todayEnd = endOfDay(today);

  return isBefore(visibleEnd, todayEnd) ? todayEnd : visibleEnd;
};

export const getInitialVisibleEndDate = (
  shifts: AvailableShift[],
  selectedMonth: Date,
  viewStartDate: Date,
  today: Date,
): Date => {
  if (isViewingCurrentMonth(selectedMonth, today)) {
    const currentWeekEnd = getCurrentWeekEnd(today);
    let visibleEnd = currentWeekEnd;

    const shiftsInCurrentWeek = countShiftsInRange(shifts, viewStartDate, currentWeekEnd);

    if (shiftsInCurrentWeek < LOW_DEMAND_SHIFT_THRESHOLD) {
      const currentWeekStart = startOfWeek(today, { weekStartsOn: WEEK_STARTS_ON });
      visibleEnd = endOfWeek(addWeeks(currentWeekStart, 1), { weekStartsOn: WEEK_STARTS_ON });
    }

    return ensureVisibleEndIncludesToday(visibleEnd, today);
  }

  const firstWeekEnd = getCurrentWeekEnd(startOfMonth(selectedMonth));
  return capToMonthEnd(firstWeekEnd, selectedMonth);
};

export const getNextVisibleEndDate = (currentVisibleEnd: Date, selectedMonth: Date): Date => {
  const currentWeekStart = startOfWeek(currentVisibleEnd, { weekStartsOn: WEEK_STARTS_ON });
  const nextWeekEnd = endOfWeek(addWeeks(currentWeekStart, 1), { weekStartsOn: WEEK_STARTS_ON });

  return capToMonthEnd(nextWeekEnd, selectedMonth);
};

export const hasMoreShiftsInMonth = (
  shifts: AvailableShift[],
  selectedMonth: Date,
  viewStartDate: Date,
  visibleEndDate: Date,
): boolean => {
  const endOfVisible = endOfDay(visibleEndDate);
  const monthEnd = endOfDay(endOfMonth(selectedMonth));

  if (!isBefore(endOfVisible, monthEnd)) return false;

  return shifts.some((shift) => {
    if (!isShiftInSelectedMonth(shift.date, selectedMonth)) return false;

    const shiftDate = getShiftDate(shift);
    if (!shiftDate || isBefore(shiftDate, startOfDay(viewStartDate))) return false;

    return isAfter(shiftDate, endOfVisible);
  });
};

export const getAvailableDateKeysFromShifts = (shifts: AvailableShift[], minSelectableDate: Date): string[] => {
  const minDateKey = toDateKeyFromDate(startOfDay(minSelectableDate));
  const uniqueDateKeys = new Set<string>();

  shifts.forEach((shift) => {
    if (shift.date < minDateKey) return;

    uniqueDateKeys.add(shift.date);
  });

  return Array.from(uniqueDateKeys);
};

export const filterShiftsForCalendar = ({
  shifts,
  selectedMonth,
  selectedDate,
  viewStartDate,
  visibleEndDate,
  today,
}: {
  shifts: AvailableShift[];
  selectedMonth: Date;
  selectedDate?: Date;
  viewStartDate: Date;
  visibleEndDate: Date;
  today: Date;
}): AvailableShift[] => {
  if (selectedDate) {
    return shifts.filter((shift) => doesShiftDateKeyMatch(shift.date, selectedDate));
  }

  return shifts.filter((shift) => {
    if (!isShiftInDateRange(shift.date, viewStartDate, visibleEndDate)) return false;

    if (isViewingCurrentMonth(selectedMonth, today)) {
      return true;
    }

    return isShiftInSelectedMonth(shift.date, selectedMonth);
  });
};
